import {
  AgentBillingModel,
  AgentLiteModel,
  AgentModel,
  getAgentKey,
  getAgentsKey,
  useGetAgent,
  useGetAgents,
} from 'api-hooks/agent';
import { Caption } from 'baseui/form-control/styled-components';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { string2money } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import BillingAccountTooltipList from 'legacy-modules/master/common/billing-account-list';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: AgentLiteModel | AgentModel): Option {
  return {
    id: item.id,
    label: item.name,
    extra: {
      name: item.name,
      email: item.email,
      phone: item.phone,
      description: item.description,
    },
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value: OnChangeParams) => void;
  onAfterDetailChange?: (value: AgentModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
  isShowBalance?: boolean;
  isShowAccountInfo?: boolean;
  remainingTransactionQuota?: number | null;
}

export default function AgentSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:agent'),
    placeholder = t('common:select_agent'),
    isShowBalance = false,
    isShowAccountInfo,
    remainingTransactionQuota,
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });
  const [balanceText, setBalanceText] = React.useState<number>(0);
  const [billingAccounts, setBillingAccounts] = React.useState<
    AgentBillingModel[]
  >([]);

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('agents');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetAgents,
    useDetailLazyQueryHook: useGetAgent,
    listKeyFunction: getAgentsKey,
    detailKeyFunction: getAgentKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.AgentsCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, AgentModel);
        setBalanceText((newResult.data as AgentModel).balance);
        isShowAccountInfo &&
          setBillingAccounts((newResult.data as AgentModel).billings);
        props.onAfterDetailChange?.(newResult.data);

        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isShowAccountInfo, props.onAfterDetailChange],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      filterOptions={null}
      overrides={overrides}
      caption={
        <>
          {isShowBalance && (
            <Caption>
              {t('common:balance_extra', {
                extra: string2money(balanceText),
              })}
            </Caption>
          )}
          {isShowAccountInfo && (
            <BillingAccountTooltipList data={billingAccounts} />
          )}
          {remainingTransactionQuota && (
            <Caption>
              {t('common:remaining_transaction_quota', {
                extra: string2money(remainingTransactionQuota),
              })}
            </Caption>
          )}
        </>
      }
      {...selectProps}
    />
  );
}
